import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryUsers } from '@/features/users/data';
import { useIsAuthenticated } from '@/shared/hooks/auth';
import { queryKeys } from '@/shared/hooks/queryKeys';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import type { OrganisationRole } from '@flowqa/shared';

export const useCurrentOrgRole = (
  userId: string | null | undefined,
  options?: { enabled?: boolean; organisationId?: string | null },
): { role: OrganisationRole | null; isAdmin: boolean; isLoading: boolean } => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const organisationId = options?.organisationId ?? selectedOrganisationId ?? null;
  const query = useQuery({
    queryKey: queryKeys.currentUserRole(userId, organisationId),
    enabled: isAuthenticated && Boolean(userId && organisationId) && (options?.enabled ?? true),
    queryFn: async () =>
      await queryUsers(organisationId, {
        page: 1,
        pageSize: 1,
        sorts: [{ field: 'email', direction: 'asc' }],
        filters: buildEqFilter('id', userId),
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const role = useMemo<OrganisationRole | null>(() => {
    const items = query.data?.items;
    if (!items || items.length === 0) return null;
    return items[0].role ?? null;
  }, [query.data?.items]);

  return { role, isAdmin: role === 'ADMIN', isLoading: query.isLoading };
};
